import { observer } from 'mobx-react-lite'
import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import Callback from '@src/app/Callback/Callback'
import StripeCallback from '@src/app/StripeCallback'
import ThirdPartyAuthorize from '@src/app/ThirdPartyAuthorize/ThirdPartyAuthorize'
import ConnectedAppSkeleton from '@src/app/components/ConnectedAppSkeleton'
import { useAppStore } from '@src/app/context'
import { useEmitFirstRender } from '@src/app/electron'
import NativeLogin from '@src/app/login/NativeLogin'
import { UniversalLoginRoute } from '@src/app/universal-login'

import AuthGuard from './AuthGuard'
import Redirect from './Redirect'
import RouterAnalytics from './RouterAnalytics'
import VerificationRoute from './VerificationRoute'

import Flag from '@odyssey/feature-flags/Flag'
import useMetaThemeColor from '@odyssey/hooks/useMetaThemeColor/useMetaThemeColor'

const Onboarding = lazy(() => import('@src/app/onboarding/Onboarding'))
const LoggedInRoot = lazy(() => import('@src/app/LoggedInRoot'))
const FirstRunInbox = lazy(
  () => import('@src/app/onboarding/FirstRunInbox/FirstRunInbox'),
)

const Router = () => {
  const app = useAppStore()

  useEmitFirstRender()
  useMetaThemeColor()

  if (app.service.authV2.isInitializing) {
    return <ConnectedAppSkeleton />
  }

  return (
    <>
      <RouterAnalytics />
      <Suspense fallback={<ConnectedAppSkeleton />}>
        <Routes>
          <Route path="native/login/*" element={<NativeLogin />} />
          <Route path="verification/*" element={<VerificationRoute />} />
          <Route
            path="*"
            element={
              app.isLoggedIn ? (
                <AuthGuard>
                  <Outlet />
                </AuthGuard>
              ) : (
                <UniversalLoginRoute />
              )
            }
          >
            <Route path="callback/stripe/*" element={<StripeCallback />} />
            <Route path="callback/:provider/*" element={<Callback />} />
            <Route path="authorize/*" element={<ThirdPartyAuthorize />} />
            <Route path="onboarding/*" element={<Onboarding />} />
            <Route
              path="activation/*"
              element={
                <Flag flag="webFirstRunInbox" fallback={<Navigate to="/" />}>
                  <FirstRunInbox />
                </Flag>
              }
            />
            <Route
              path="*"
              element={
                app.storesLoaded && app.needsOnboarding ? (
                  // eslint-disable-next-line @typescript-eslint/no-deprecated
                  <Redirect to="/onboarding" />
                ) : (
                  <LoggedInRoot />
                )
              }
            />
          </Route>
        </Routes>
      </Suspense>
    </>
  )
}

export default observer(Router)
